import React, { useState } from 'react';
import { Popover, OverlayTrigger, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faShieldAlt, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { Cookies } from 'react-cookie-consent';

export const COOKIE_CONSENT_NAME = "CD12_CookieAcceptv12022";

export const DISCLAIMER_TEXT = 
  "The developers, owners, and all involved parties of this website, the Beer Bank application, and every ecosystem component DO NOT recommend, encourage, or promote the usage or consumption of alcohol. Furthermore, they strictly advise against and DO NOT endorse unresponsive, underage, or spontaneous actions with finances (specifically in the context of cryptocurrencies, digital tokens, and Web3/NFT assets). All fitness, beer, and health telemetry data are provided solely for personal informational tracking and wellness demonstration purposes.";

const DisclaimerPopup = ({ variant = "header", placement = "bottom", onAcceptConsent }) => {
  const [showModal, setShowModal] = useState(false);

  const handleUnderstand = () => {
    try {
      Cookies.set(COOKIE_CONSENT_NAME, "true", { expires: 150 });
    } catch (e) {}

    if (typeof onAcceptConsent === 'function') {
      onAcceptConsent();
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cookieConsentAccepted', {
        detail: { cookieName: COOKIE_CONSENT_NAME }
      }));
    }

    setShowModal(false);
  };

  const popover = (
    <Popover id="compliance-disclaimer-popover" className="shadow-lg border-warning" style={{ maxWidth: '360px', zIndex: 1099 }}>
      <Popover.Header as="h6" className="bg-warning text-white fw-bold d-flex align-items-center">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        Legal &amp; Compliance Notice
      </Popover.Header>
      <Popover.Body className="small text-dark" style={{ lineHeight: '1.5' }}>
        <p className="mb-2">
          <strong>Notice:</strong> {DISCLAIMER_TEXT}
        </p>
        <div className="border-top pt-2 text-muted" style={{ fontSize: '0.75rem' }}>
          Compliance standard: Responsible Health &amp; Financial Governance (v0.1.29+).
        </div>
      </Popover.Body>
    </Popover>
  );

  if (variant === "footer") {
    return (
      <>
        <OverlayTrigger trigger={['hover', 'focus']} placement={placement} overlay={popover}>
          <button
            type="button"
            className="btn btn-outline-warning btn-sm rounded-pill px-2 py-0 ms-2 fw-bold d-inline-flex align-items-center"
            style={{ fontSize: "0.75rem", height: "22px", verticalAlign: "middle" }}
            onClick={() => setShowModal(true)}
            aria-label="View Legal Notice, Disclaimers & Consent"
            data-testid="disclaimer-popup-trigger"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            <span>[Legal Notice &amp; Disclaimers]</span>
          </button>
        </OverlayTrigger>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="md">
          <Modal.Header closeButton className="bg-warning text-white">
            <Modal.Title className="fs-6 fw-bold">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              HealthDash &amp; Beer Bank Compliance &amp; Consent Notice
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="small text-dark">
            <p>{DISCLAIMER_TEXT}</p>
            <div className="border-top pt-2 text-muted" style={{ fontSize: '0.75rem' }}>
              Compliance standard: Responsible Health &amp; Financial Governance (v0.1.29+).
              <br />
              By clicking <strong>"I Understand and Agree"</strong>, you acknowledge these disclaimers and grant consent for cookie usage and terms acceptance.
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between align-items-center">
            <span className="small text-muted" style={{ fontSize: '0.75rem' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-1" />
              Consent: Cookies &amp; Terms
            </span>
            <Button variant="warning" size="sm" className="text-white fw-bold" onClick={handleUnderstand} aria-label="I Understand and Agree">
              I Understand and Agree
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  if (variant === "compact") {
    return (
      <>
        <OverlayTrigger trigger={['hover', 'focus']} placement={placement} overlay={popover}>
          <button
            type="button"
            className="btn btn-link p-0 text-warning text-decoration-none fw-bold ms-1 d-inline-flex align-items-center"
            style={{ fontSize: "10px", verticalAlign: "middle" }}
            onClick={() => setShowModal(true)}
            aria-label="View Legal & Compliance Disclaimer"
            data-testid="disclaimer-popup-trigger"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            [Disclaimer]
          </button>
        </OverlayTrigger>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="md">
          <Modal.Header closeButton className="bg-warning text-white">
            <Modal.Title className="fs-6 fw-bold">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              HealthDash &amp; Beer Bank Compliance &amp; Consent Notice
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="small text-dark">
            <p>{DISCLAIMER_TEXT}</p>
            <div className="border-top pt-2 text-muted" style={{ fontSize: '0.75rem' }}>
              By clicking <strong>"I Understand"</strong>, you grant consent to terms and cookie usage.
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between align-items-center">
            <span className="small text-muted" style={{ fontSize: '0.75rem' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-1" />
              Consent: Cookies &amp; Terms
            </span>
            <Button variant="warning" size="sm" className="text-white fw-bold" onClick={handleUnderstand} aria-label="I Understand">
              I Understand
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return (
    <>
      <OverlayTrigger trigger={['hover', 'focus']} placement={placement} overlay={popover}>
        <button
          type="button"
          className="btn btn-outline-light btn-sm rounded-pill px-2 py-0 ms-2 text-warning border-warning fw-bold d-inline-flex align-items-center"
          style={{ fontSize: "0.75rem", cursor: "pointer", height: "24px" }}
          onClick={() => setShowModal(true)}
          aria-label="View Responsibility Notice"
          data-testid="disclaimer-popup-trigger"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
          <span>Notice</span>
        </button>
      </OverlayTrigger>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="md">
        <Modal.Header closeButton className="bg-warning text-white">
          <Modal.Title className="fs-6 fw-bold">
            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
            HealthDash &amp; Beer Bank Compliance &amp; Consent Notice
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small text-dark">
          <p>{DISCLAIMER_TEXT}</p>
          <div className="border-top pt-2 text-muted" style={{ fontSize: '0.75rem' }}>
            By clicking <strong>"I Understand"</strong>, you acknowledge these terms and grant consent for cookie usage.
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <span className="small text-muted" style={{ fontSize: '0.75rem' }}>
            <FontAwesomeIcon icon={faCheckCircle} className="text-success me-1" />
            Consent: Cookies &amp; Terms
          </span>
          <Button variant="warning" size="sm" className="text-white fw-bold" onClick={handleUnderstand} aria-label="I Understand">
            I Understand
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DisclaimerPopup;
